import { extractSortDataFromSorterResult } from '@supplement-bacon/react-toolkit/utils'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import { TablePaginationConfig } from 'antd'
import { FilterValue, SorterResult, SortOrder } from 'antd/es/table/interface'
import { useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

// Every table starts at the toolkit's default page size; the URL only carries
// another choice.
const DEFAULT_PAGE_SIZE = initialQuery.pagination?.pageSize ?? 20

export interface TableUrlParams {
  page: number
  pageSize: number
  sort: {
    columnKey?: string
    order?: SortOrder
  }
  filters: Record<string, any>
  search: Record<string, any>
}

interface Props {
  onParamsChange: (params: TableUrlParams) => void
}

const getParams = (urlParams: URLSearchParams): TableUrlParams => {
  const filters: Record<string, any> = {}
  const search: Record<string, any> = {}

  const keys = Array.from(new Set(urlParams.keys()))

  keys.forEach((key) => {
    const cleanKey = key.replace(/^[fs]_/, '')

    if (key.startsWith('f_')) {
      filters[cleanKey] = urlParams.getAll(key)
    } else if (key.startsWith('s_')) {
      search[cleanKey] = urlParams.get(key)
    }
  })

  return {
    filters,
    search,
    page: Number(urlParams.get('page') ?? 1),
    pageSize: Number(urlParams.get('pageSize') ?? DEFAULT_PAGE_SIZE),
    sort: {
      columnKey: urlParams.get('columnKey') ?? undefined,
      order: (urlParams.get('order') as SortOrder) ?? undefined,
    },
  }
}

export const useTableUrlSync = <T,>({ onParamsChange }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const onParamsChangeRef = useRef(onParamsChange)

  useEffect(() => {
    onParamsChangeRef.current = onParamsChange
  }, [onParamsChange])

  const params = useMemo(() => getParams(searchParams), [searchParams])

  useEffect(() => {
    onParamsChangeRef.current(params)
  }, [params])

  // TableFilters
  const handleFiltersChange = (filters: any, search: any) => {
    const nextParams = new URLSearchParams(searchParams)

    Array.from(nextParams.keys()).forEach((key) => {
      if (key.startsWith('f_') || key.startsWith('s_')) nextParams.delete(key)
    })
    nextParams.delete('page')

    const syncToUrl = (data: any, prefix: string) => {
      Object.entries(data).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return

        if (Array.isArray(value)) {
          value.forEach((v) => nextParams.append(`${prefix}${key}`, String(v)))
        } else {
          nextParams.set(`${prefix}${key}`, String(value))
        }
      })
    }

    syncToUrl(filters, 'f_')
    syncToUrl(search, 's_')

    setSearchParams(nextParams)
  }

  // SBTable
  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<T> | SorterResult<T>[]
  ) => {
    const nextParams = new URLSearchParams(searchParams)

    const pageSize = pagination.pageSize ?? params.pageSize
    if (pageSize === DEFAULT_PAGE_SIZE) {
      nextParams.delete('pageSize')
    } else {
      nextParams.set('pageSize', String(pageSize))
    }

    // A new page size reshuffles every page: start over from the first one.
    const pageSizeChanged = pageSize !== params.pageSize
    if (!pageSizeChanged && pagination.current && pagination.current > 1) {
      nextParams.set('page', String(pagination.current))
    } else {
      nextParams.delete('page')
    }

    const { columnKey, order } = extractSortDataFromSorterResult(sorter)
    if (columnKey && order) {
      nextParams.set('columnKey', String(columnKey))
      nextParams.set('order', order)
    } else {
      nextParams.delete('columnKey')
      nextParams.delete('order')
    }

    setSearchParams(nextParams)
  }

  return {
    searchParams,
    setSearchParams,
    handleTableChange,
    handleFiltersChange,
    params,
  }
}
